import { useRef, useEffect, useState } from 'react';

/* ─── Grid size variants ─── */
const COLS_D = 120, ROWS_D = 68;   // desktop  (14px cells on 1680px)
const COLS_M = 64,  ROWS_M = 38;   // mobile   (~6px cells on 390px)

function getPaintGrid() {
  const mobile = window.innerWidth < 768;
  const C = mobile ? COLS_M : COLS_D;
  const R = mobile ? ROWS_M : ROWS_D;
  return { COLS: C, ROWS: R, TOTAL: C * R, mobile };
}

/* ─── Brush radii in grid cells per size option ─── */
const BRUSH_RADII_D = [1.5, 3, 5, 8];   // desktop
const BRUSH_RADII_M = [2,   4, 7, 11];  // mobile — larger to compensate finger imprecision

/* ─── Char pools ─── */
const POOL_LOW   = ['·', '1', '2', '·', '1'];
const POOL_MID   = ['3', '4', '5', '4', '3'];
const POOL_HIGH  = ['8', '9', '8', '9', '7'];

const DENSITY_POOLS = {
  LIGHT: POOL_LOW,
  MED:   POOL_MID,
  HEAVY: POOL_HIGH,
};

function getCharForDensitySetting(setting) {
  const pool = DENSITY_POOLS[setting] || POOL_MID;
  return pool[Math.floor(Math.random() * pool.length)];
}

function mutateChar(ch) {
  if (POOL_LOW.includes(ch))  return POOL_LOW[Math.floor(Math.random()  * POOL_LOW.length)];
  if (POOL_MID.includes(ch))  return POOL_MID[Math.floor(Math.random()  * POOL_MID.length)];
  if (POOL_HIGH.includes(ch)) return POOL_HIGH[Math.floor(Math.random() * POOL_HIGH.length)];
  return ch;
}

const FONT_M = "'Space Mono', monospace";

const RED_CROSSHAIR =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E" +
  "%3Cline x1='10' y1='0' x2='10' y2='7' stroke='%23E8351A' stroke-width='1.5'/%3E" +
  "%3Cline x1='10' y1='13' x2='10' y2='20' stroke='%23E8351A' stroke-width='1.5'/%3E" +
  "%3Cline x1='0' y1='10' x2='7' y2='10' stroke='%23E8351A' stroke-width='1.5'/%3E" +
  "%3Cline x1='13' y1='10' x2='20' y2='10' stroke='%23E8351A' stroke-width='1.5'/%3E" +
  "%3Ccircle cx='10' cy='10' r='2.5' stroke='%23E8351A' stroke-width='1.5' fill='none'/%3E" +
  "%3C/svg%3E\") 10 10, crosshair";

/* ═══════════════════════════════════════════════════════════
   PAINT WINDOW
═══════════════════════════════════════════════════════════ */
export default function PaintWindow({ onClose }) {
  const canvasRef      = useRef(null);
  const rafIdRef       = useRef(null);
  const isDrawingRef   = useRef(false);
  const dimRef         = useRef({ cW: 0, cH: 0 });
  const gridRef        = useRef(null);    // set in mount effect
  const paintGridRef   = useRef(null);    // Float32Array, set in mount effect
  const paintCharsRef  = useRef(null);    // string[], set in mount effect
  const lastPosRef     = useRef(null);

  const brushIdxRef    = useRef(1);
  const densityRef     = useRef('MED');
  const eraseModeRef   = useRef(false);

  const [brushIdx, setBrushIdxState]   = useState(1);
  const [density, setDensityState]     = useState('MED');
  const [eraseMode, setEraseModeState] = useState(false);
  const [isMobile, setIsMobile]        = useState(() => window.innerWidth < 768);

  const setBrushIdx  = (v) => { brushIdxRef.current  = v; setBrushIdxState(v);  };
  const setDensity   = (v) => { densityRef.current   = v; setDensityState(v);   };
  const setEraseMode = (v) => { eraseModeRef.current = v; setEraseModeState(v); };

  /* ─── Mount: determine grid size, allocate arrays, restore localStorage ─── */
  useEffect(() => {
    const grid = getPaintGrid();
    gridRef.current      = grid;
    setIsMobile(grid.mobile);

    paintGridRef.current  = new Float32Array(grid.TOTAL);
    paintCharsRef.current = new Array(grid.TOTAL).fill(' ');

    /* Restore from localStorage */
    try {
      const saved = localStorage.getItem('stacyverse_paint');
      if (saved) {
        const { grid: g, chars, cols, rows } = JSON.parse(saved);
        if (cols === grid.COLS && rows === grid.ROWS && g && chars) {
          paintGridRef.current  = new Float32Array(g);
          paintCharsRef.current = [...chars];
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  /* ─── Save to localStorage on close ─── */
  const handleClose = () => {
    try {
      const grid = gridRef.current;
      if (grid && paintGridRef.current) {
        localStorage.setItem('stacyverse_paint', JSON.stringify({
          grid:  Array.from(paintGridRef.current),
          chars: paintCharsRef.current,
          cols:  grid.COLS,
          rows:  grid.ROWS,
        }));
      }
    } catch (e) { /* quota */ }
    onClose();
  };

  /* ─── Canvas resize ─── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const applySize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const grid = gridRef.current;
      if (grid) {
        dimRef.current = {
          cW: canvas.width  / grid.COLS,
          cH: canvas.height / grid.ROWS,
        };
      }
    };
    applySize();
    const obs = new ResizeObserver(applySize);
    obs.observe(canvas);
    return () => obs.disconnect();
  }, []);

  /* ─── RAF render loop ─── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';

    function render() {
      /* Wait until everything is initialized */
      if (!gridRef.current || !paintGridRef.current) {
        rafIdRef.current = requestAnimationFrame(render);
        return;
      }

      const { COLS, ROWS, TOTAL, mobile } = gridRef.current;   // eslint-disable-line no-unused-vars
      const { cW, cH } = dimRef.current;
      const W = canvas.width, H = canvas.height;

      if (!W || !H || !cW) { rafIdRef.current = requestAnimationFrame(render); return; }

      ctx.fillStyle = '#F2EFE4';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#0D0D0D';

      const grid  = paintGridRef.current;
      const chars = paintCharsRef.current;

      /* Bucket by fontSize */
      const bkts = {};
      for (let i = 0; i < TOTAL; i++) {
        if (grid[i] < 0.01) continue;
        const fsMax = mobile ? 16 : 24;
        const sz    = Math.max(3, Math.round(3 + grid[i] * fsMax));
        if (!bkts[sz]) bkts[sz] = [];
        bkts[sz].push(i);
      }

      for (const sz in bkts) {
        ctx.font = `${sz}px Ndot55`;
        for (const idx of bkts[sz]) {
          const col = idx % COLS;
          const row = Math.floor(idx / COLS);
          ctx.globalAlpha = grid[idx] * 0.92;
          ctx.fillText(chars[idx], (col + 0.5) * cW, (row + 0.5) * cH);
        }
      }

      ctx.globalAlpha  = 1;
      rafIdRef.current = requestAnimationFrame(render);
    }

    document.fonts.ready.then(() => {
      rafIdRef.current = requestAnimationFrame(render);
    });

    return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
  }, []);

  /* ─── Organic mutation — stays within each cell's own pool ─── */
  useEffect(() => {
    const interval = setInterval(() => {
      if (!paintGridRef.current || !paintCharsRef.current) return;
      const grid  = paintGridRef.current;
      const chars = paintCharsRef.current;
      const total = gridRef.current ? gridRef.current.TOTAL : 0;
      let count = 0;
      for (let i = 0; i < total; i++) {
        if (grid[i] > 0.01 && Math.random() < 0.1) {
          chars[i] = mutateChar(chars[i]);
          if (++count >= 60) break;
        }
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  /* ─── Core cell painter ─── */
  const paintCells = (x, y) => {
    if (!gridRef.current || !paintGridRef.current) return;
    const { COLS, ROWS, TOTAL, mobile } = gridRef.current;
    const { cW, cH } = dimRef.current;
    if (!cW || !cH) return;

    const grid   = paintGridRef.current;
    const chars  = paintCharsRef.current;
    const radii  = mobile ? BRUSH_RADII_M : BRUSH_RADII_D;
    const radius = radii[brushIdxRef.current];
    const erase  = eraseModeRef.current;
    const dens   = densityRef.current;

    const gcol = x / cW;
    const grow = y / cH;

    const minC = Math.max(0, Math.floor(gcol - radius));
    const maxC = Math.min(COLS - 1, Math.ceil(gcol + radius));
    const minR = Math.max(0, Math.floor(grow - radius));
    const maxR = Math.min(ROWS - 1, Math.ceil(grow + radius));

    /* Brush radius in pixel space — makes stroke circular regardless of cell AR */
    const pxRadius = radius * cW;

    for (let r = minR; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) {
        const dx = (c + 0.5) - gcol;
        const dy = (r + 0.5) - grow;
        /* Compare in pixel space so the brush is always circular */
        const pxDist = Math.sqrt((dx * cW) ** 2 + (dy * cH) ** 2);
        if (pxDist > pxRadius) continue;
        const falloff = 1 - pxDist / pxRadius;
        const i       = r * COLS + c;
        if (i >= TOTAL) continue;

        if (erase) {
          grid[i] = Math.max(0, grid[i] - falloff * 0.6);
          if (grid[i] < 0.01) chars[i] = ' ';
        } else {
          grid[i]  = Math.min(1, grid[i] + falloff * 0.85);
          chars[i] = getCharForDensitySetting(dens);
        }
      }
    }
  };

  /* ─── Paint with linear interpolation ─── */
  const paintAt = (x, y) => {
    if (!gridRef.current) return;
    const { cW, cH } = dimRef.current;
    const last = lastPosRef.current;
    if (last && cW && cH) {
      const dx   = x - last.x;
      const dy   = y - last.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const step = Math.max(cW, cH) * 0.5;
      const n    = Math.ceil(dist / step);
      for (let s = 1; s < n; s++) {
        const t = s / n;
        paintCells(last.x + dx * t, last.y + dy * t);
      }
    }
    paintCells(x, y);
    lastPosRef.current = { x, y };
  };

  /* ─── Pointer helpers ─── */
  const canvasXY = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onMouseDown = (e) => { e.preventDefault(); isDrawingRef.current = true;  lastPosRef.current = null; const p = canvasXY(e); paintAt(p.x, p.y); };
  const onMouseMove = (e) => { if (!isDrawingRef.current) return; const p = canvasXY(e); paintAt(p.x, p.y); };
  const onMouseUp   = () =>  { isDrawingRef.current = false; lastPosRef.current = null; };
  const onMouseLeave = () => { isDrawingRef.current = false; lastPosRef.current = null; };

  const onTouchStart = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    lastPosRef.current   = null;
    const t = e.touches[0];
    const r = canvasRef.current.getBoundingClientRect();
    paintAt(t.clientX - r.left, t.clientY - r.top);
  };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const t = e.touches[0];
    const r = canvasRef.current.getBoundingClientRect();
    paintAt(t.clientX - r.left, t.clientY - r.top);
  };
  const onTouchEnd = () => { isDrawingRef.current = false; lastPosRef.current = null; };

  /* ─── CLR / SAVE ─── */
  const handleClear = () => {
    if (!gridRef.current) return;
    paintGridRef.current  = new Float32Array(gridRef.current.TOTAL);
    paintCharsRef.current = new Array(gridRef.current.TOTAL).fill(' ');
    localStorage.removeItem('stacyverse_paint');
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a  = document.createElement('a');
    a.href     = canvas.toDataURL('image/png');
    a.download = 'stacyverse-paint.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ─── Style helpers ─── */
  const tbBtn = (active = false, extraStyle = {}) => ({
    fontFamily:    FONT_M,
    fontSize:      isMobile ? 10 : 8,
    border:        '1px solid rgba(13,13,13,0.3)',
    background:    active ? '#0D0D0D' : 'transparent',
    color:         active ? '#F2EFE4' : '#0D0D0D',
    padding:       isMobile ? '7px 12px' : '3px 8px',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    cursor:        RED_CROSSHAIR,
    lineHeight:    1.5,
    whiteSpace:    'nowrap',
    minHeight:     isMobile ? 36 : 'auto',
    display:       'flex',
    alignItems:    'center',
    ...extraStyle,
  });

  /* ─── Brush visual sizes ─── */
  const BRUSH_VISUAL_PX = isMobile ? [10, 16, 22, 30] : [4, 8, 12, 20];

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <div
      data-testid="paint-window"
      style={{
        position:      'fixed',
        inset:         0,
        background:    'rgba(242,239,228,0.96)',
        zIndex:        100,
        display:       'flex',
        flexDirection: 'column',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── OS Titlebar ── */}
      <div
        data-testid="paint-titlebar"
        style={{
          height:         isMobile ? 40 : 32,
          background:     '#0D0D0D',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '0 16px',
          flexShrink:     0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            <div style={{ width: 7, height: 7, background: '#E8351A' }} />
            <div style={{ width: 7, height: 7, background: '#F5C518' }} />
            <div style={{ width: 7, height: 7, background: '#00FFB2' }} />
          </div>
          <span style={{ fontFamily: FONT_M, fontSize: isMobile ? 10 : 9, color: '#F2EFE4', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            PAINT.EXE
          </span>
        </div>
        <button
          data-testid="paint-close"
          onClick={handleClose}
          style={{
            background:    'none',
            border:        'none',
            fontFamily:    FONT_M,
            fontSize:      isMobile ? 11 : 9,
            color:         'rgba(242,239,228,0.4)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            cursor:        RED_CROSSHAIR,
            padding:       isMobile ? '8px 4px' : 0,
            minHeight:     isMobile ? 44 : 'auto',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#E8351A'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(242,239,228,0.4)'; }}
        >
          × CLOSE
        </button>
      </div>

      {/* ── Toolbar ── */}
      {isMobile ? (
        /* ─── MOBILE: two-row toolbar ─── */
        <div
          data-testid="paint-toolbar"
          style={{
            background:   '#F2EFE4',
            borderBottom: '1px solid rgba(13,13,13,0.12)',
            flexShrink:   0,
          }}
        >
          {/* Row 1: SIZE + DENSITY */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', gap: 16 }}>
            {/* Brush size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: FONT_M, fontSize: 9, color: 'rgba(13,13,13,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>SZ</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {BRUSH_VISUAL_PX.map((px, idx) => (
                  <button
                    key={idx}
                    data-testid={`brush-size-${idx}`}
                    onClick={() => setBrushIdx(idx)}
                    style={{
                      width:      px,
                      height:     px,
                      minWidth:   px,
                      border:     '1px solid rgba(13,13,13,0.3)',
                      background: brushIdx === idx ? '#0D0D0D' : 'transparent',
                      cursor:     RED_CROSSHAIR,
                      padding:    0,
                      flexShrink: 0,
                      /* Make tap target larger than visual square */
                      margin:     `${Math.max(0, (24 - px) / 2)}px`,
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: 'rgba(13,13,13,0.12)', flexShrink: 0 }} />

            {/* Density */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: FONT_M, fontSize: 9, color: 'rgba(13,13,13,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>DEN</span>
              {['LIGHT', 'MED', 'HEAVY'].map((d) => (
                <button
                  key={d}
                  data-testid={`density-${d.toLowerCase()}`}
                  onClick={() => setDensity(d)}
                  style={tbBtn(density === d)}
                >
                  {d === 'LIGHT' ? 'L' : d === 'MED' ? 'M' : 'H'}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: ERASE + CLR + SAVE */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '4px 14px 8px', gap: 10 }}>
            <button
              data-testid="erase-toggle"
              onClick={() => setEraseMode(!eraseMode)}
              style={tbBtn(eraseMode)}
              onMouseEnter={(e) => { if (!eraseMode) { e.currentTarget.style.background = '#E8351A'; e.currentTarget.style.color = '#F2EFE4'; } }}
              onMouseLeave={(e) => { if (!eraseMode) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0D0D0D'; } }}
            >
              ERASE
            </button>
            <button
              data-testid="paint-clear"
              onClick={handleClear}
              style={tbBtn(false)}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#E8351A'; e.currentTarget.style.color = '#F2EFE4'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0D0D0D'; }}
            >
              CLR
            </button>
            <button
              data-testid="paint-save"
              onClick={handleSave}
              style={tbBtn(false)}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#0D0D0D'; e.currentTarget.style.color = '#F2EFE4'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0D0D0D'; }}
            >
              SAVE ↓
            </button>
            <div style={{ marginLeft: 'auto', fontFamily: FONT_M, fontSize: 7, color: 'rgba(13,13,13,0.2)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              DRAW WITH TOUCH
            </div>
          </div>
        </div>
      ) : (
        /* ─── DESKTOP: single-row toolbar ─── */
        <div
          data-testid="paint-toolbar"
          style={{
            height:       44,
            background:   '#F2EFE4',
            borderBottom: '1px solid rgba(13,13,13,0.12)',
            display:      'flex',
            alignItems:   'center',
            padding:      '0 16px',
            gap:          20,
            flexShrink:   0,
            overflowX:    'auto',
          }}
        >
          {/* Brush size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontFamily: FONT_M, fontSize: 8, color: 'rgba(13,13,13,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>SIZE</span>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {BRUSH_VISUAL_PX.map((px, idx) => (
                <button
                  key={idx}
                  data-testid={`brush-size-${idx}`}
                  onClick={() => setBrushIdx(idx)}
                  style={{
                    width:      px,
                    height:     px,
                    minWidth:   px,
                    border:     '1px solid rgba(13,13,13,0.3)',
                    background: brushIdx === idx ? '#0D0D0D' : 'transparent',
                    cursor:     RED_CROSSHAIR,
                    padding:    0,
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ width: 1, height: 20, background: 'rgba(13,13,13,0.12)', flexShrink: 0 }} />

          {/* Density */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontFamily: FONT_M, fontSize: 8, color: 'rgba(13,13,13,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>DENSITY</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {['LIGHT', 'MED', 'HEAVY'].map((d) => (
                <button
                  key={d}
                  data-testid={`density-${d.toLowerCase()}`}
                  onClick={() => setDensity(d)}
                  style={tbBtn(density === d)}
                  onMouseEnter={(e) => { if (density !== d) e.currentTarget.style.background = 'rgba(13,13,13,0.08)'; }}
                  onMouseLeave={(e) => { if (density !== d) e.currentTarget.style.background = 'transparent'; }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div style={{ width: 1, height: 20, background: 'rgba(13,13,13,0.12)', flexShrink: 0 }} />

          <button
            data-testid="erase-toggle"
            onClick={() => setEraseMode(!eraseMode)}
            style={tbBtn(eraseMode)}
            onMouseEnter={(e) => { if (!eraseMode) { e.currentTarget.style.background = '#E8351A'; e.currentTarget.style.color = '#F2EFE4'; } }}
            onMouseLeave={(e) => { if (!eraseMode) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0D0D0D'; } }}
          >
            ERASE
          </button>

          <button
            data-testid="paint-clear"
            onClick={handleClear}
            style={tbBtn(false)}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#E8351A'; e.currentTarget.style.color = '#F2EFE4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0D0D0D'; }}
          >
            CLR
          </button>

          <button
            data-testid="paint-save"
            onClick={handleSave}
            style={tbBtn(false)}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#0D0D0D'; e.currentTarget.style.color = '#F2EFE4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0D0D0D'; }}
          >
            SAVE ↓
          </button>

          <div style={{ marginLeft: 'auto', fontFamily: FONT_M, fontSize: 7, color: 'rgba(13,13,13,0.2)', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap', flexShrink: 0 }}>
            DRAW WITH MOUSE OR TOUCH
          </div>
        </div>
      )}

      {/* ── Paint Canvas ── */}
      <canvas
        ref={canvasRef}
        data-testid="paint-canvas"
        style={{
          flex:        1,
          display:     'block',
          background:  '#F2EFE4',
          cursor:      RED_CROSSHAIR,
          touchAction: 'none',
          width:       '100%',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      />
    </div>
  );
}
